import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronRight, X } from "lucide-react";
import { useResolvedNavigation } from "./hooks/useResolvedNavigation";
import { NavigationHeader } from "./components/NavigationHeader";
import { NavigationMenu } from "./components/NavigationMenu";
import { NavigationFooter } from "./components/NavigationFooter";
import LogoutModal from "./components/LogoutModal";
import { paths } from "../../_app/router/paths";
import { useMediaQuery } from "../../_hooks/useMediaQuery";
import "./Navigation.scss";

// Below this width there's no room to spare 252px of a phone-width screen
// for the settings rail's labels - it collapses to the same icon-only
// width as MainNav's collapsed state (see .nav--settings in Navigation.scss).
const NARROW_QUERY = "(max-width: 768px)";

/**
 * Settings navigation rail. Always fully expanded on desktop, no
 * hover-peek, no lock — it's a distinct layout from MainNav, not another
 * state of it. Below NARROW_QUERY it collapses to an icon-only rail
 * (mirroring MainNav's collapsed state) since there's no room to fully
 * expand.
 *
 * MainNav's desktop equivalent of "see it expanded" is hovering — that's
 * unavailable here (no hover-peek at all) and disabled on touch devices
 * anyway. So below NARROW_QUERY a small tab fixed to the rail's own edge
 * lets you tap it open as a floating overlay (no content reflow, matching
 * how MainNav's hover-peek behaves) instead of being permanently icon-only.
 */
export default function SettingsNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  const isNarrow = useMediaQuery(NARROW_QUERY);

  const currentNavItems = useResolvedNavigation(true);
  const handleSettingsBack = () => navigate(paths.company);

  // Picking a destination is the natural "close" gesture - without this the
  // overlay would still be sitting open on top of the page you just navigated to.
  useEffect(() => {
    setIsMobileExpanded(false);
  }, [location.pathname]);

  // The expanded state is only meaningful below NARROW_QUERY - resizing back
  // out of it (e.g. rotating a tablet) must not leave it stuck open once the
  // rail returns to its always-expanded desktop layout.
  useEffect(() => {
    if (!isNarrow) setIsMobileExpanded(false);
  }, [isNarrow]);

  const mini = isNarrow && !isMobileExpanded;
  const railStateClass = !isNarrow
    ? ""
    : isMobileExpanded
      ? "nav--settings-mobile-expanded"
      : "nav--settings-collapsed";

  return (
    <>
      {isNarrow && isMobileExpanded && (
        <div className="nav-mobile-backdrop" onClick={() => setIsMobileExpanded(false)} />
      )}
      <div className={`nav nav--settings ${railStateClass}`}>
        <NavigationHeader
          isSettingsMode={true}
          handleSettingsBack={handleSettingsBack}
          mini={mini}
        />
        <NavigationMenu
          isSettingsMode={true}
          currentNavItems={currentNavItems}
          mini={mini}
        />
        <NavigationFooter
          onLogoutClick={() => setIsLogoutOpen(true)}
          mini={mini}
          isSettingsMode
        />
        {isNarrow && (
          <button
            type="button"
            className="nav__mobile-toggle"
            onClick={() => setIsMobileExpanded((open) => !open)}
            aria-label={isMobileExpanded ? "Collapse settings menu" : "Expand settings menu"}
            aria-expanded={isMobileExpanded}
          >
            {isMobileExpanded ? <X size={16} /> : <ChevronRight size={16} />}
          </button>
        )}
        <LogoutModal isOpen={isLogoutOpen} onClose={() => setIsLogoutOpen(false)} />
      </div>
    </>
  );
}
